import React, { useState } from 'react';
import Modal from 'react-bootstrap/lib/Modal';
import Button from 'react-bootstrap/lib/Button';

import { changeTokensAllowed, getAllowedTokensOptions } from './PoliciesUtils';
import { FEATURE_FLAGS_NAMES, POLICIES_HITS_TIME_UNITS_TYPES } from '@silverfort-nodelibs/common/utils/consts';

import styles from './CssModules/PoliciesSettingsModal.module.css';
import useFeature from '../SystemSettings/hooks/featureFlags/useFeature';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import { Dropdown } from '@silverfort/sf-components';

const PoliciesSettingsModal = ({ onApprove, onCancel, policyConfiguration, defaultPolicy }) => {
    const [newConfiguration, setNewConfiguration] = useState({
        policiesHitsTimeUnit: policyConfiguration.policiesHitsTimeUnit,
        policiesHitsThreshold: policyConfiguration.policiesHitsThreshold,
        defaultAllowedTokenTypes: policyConfiguration.defaultAllowedTokenTypes
    });
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedTokens, setSelectedTokens] = useState(newConfiguration.defaultAllowedTokenTypes);

    const onPoliciesConfigurationChanged = (fieldName, newValue, event) => {
        setErrorMessage('');
        if (fieldName === 'policiesHitsThreshold') {
            const parsedValue = newValue !== '' ? parseInt(newValue) : '';
            if (parsedValue < 0) {
                newValue = 1;
            } else if (!event.target.validity.valid) {
                return;
            }
        }
        setNewConfiguration({ ...newConfiguration, [fieldName]: newValue });
    };

    const SaveConfigurationClicked = () => {
        if (newConfiguration.policiesHitsThreshold === '') {
            setErrorMessage('Policy Hits time cannot be empty');
            return;
        }
        if (
            newConfiguration.policiesHitsTimeUnit === POLICIES_HITS_TIME_UNITS_TYPES.HOURS.value &&
            (newConfiguration.policiesHitsThreshold < 1 || newConfiguration.policiesHitsThreshold > 24)
        ) {
            setErrorMessage('Select a period between 1 and 24 hours');
            return;
        }

        if (
            newConfiguration.policiesHitsTimeUnit === POLICIES_HITS_TIME_UNITS_TYPES.DAYS.value &&
            (newConfiguration.policiesHitsThreshold < 1 || newConfiguration.policiesHitsThreshold > 90)
        ) {
            setErrorMessage('Select a period between 1 and 90 days');
            return;
        }

        if (
            newConfiguration.policiesHitsTimeUnit === POLICIES_HITS_TIME_UNITS_TYPES.WEEKS.value &&
            (newConfiguration.policiesHitsThreshold < 1 || newConfiguration.policiesHitsThreshold > 52)
        ) {
            setErrorMessage('Select a period between 1 and 52 weeks');
            return;
        }

        if (newConfiguration.defaultAllowedTokenTypes.length === 0) {
            setErrorMessage('Default tokens cannot be empty');
            return;
        }

        const saveSuccess = onApprove(newConfiguration);
        if (!saveSuccess) {
            setErrorMessage('Failed to save policy settings. Please try again later');
        }
    };

    const onTokensAllowedChanged = (event) => {
        const newFieldValue = changeTokensAllowed(event, newConfiguration.defaultAllowedTokenTypes);
        onPoliciesConfigurationChanged('defaultAllowedTokenTypes', newFieldValue);
    };

    const systemMode = useFeature(FEATURE_FLAGS_NAMES.SystemMode);
    const isSaas = systemMode !== SYSTEM_MODES.ON_PREM;

    const allowedTokensOptions = getAllowedTokensOptions(
        selectedTokens,
        policyConfiguration.ConfiguredTokens,
        defaultPolicy.desktop_enabled,
        defaultPolicy.ping_fed_enabled,
        defaultPolicy.mode_type,
        defaultPolicy.radius_enabled,
        isSaas
    );

    const handleClickTokenOption = (value) => {
        if (value !== undefined) {
            let newSelectedTokens;
            if (selectedTokens.includes(value)) {
                newSelectedTokens = selectedTokens.filter((token) => token !== value);
            } else {
                newSelectedTokens = [...selectedTokens, value];
            }

            const newSelectedTokensAfterValidations = changeTokensAllowed(newSelectedTokens, selectedTokens);
            setSelectedTokens(newSelectedTokensAfterValidations);
        }
    };

    return (
        <Modal
            id={styles.policiesSettingsModal}
            show={true}
            keyboard={true}
            enforceFocus={false}
            className="z-[5000]"
            backdropClassName="z-[5000]"
            backdrop="static">
            <Modal.Header>
                <Modal.Title>{'Policy Settings'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className={`${styles.policiesConfigurationField}`}>
                    <div className={styles.policiesConfigurationFieldLabel}>Policy hits period</div>
                    <div className={`${styles.policiesHitsFieldData} ${styles.policiesConfigurationFieldData}`}>
                        <input
                            className={''}
                            id={'policiesHitsThresholdInput'}
                            value={newConfiguration.policiesHitsThreshold}
                            type="number"
                            min="1"
                            pattern="\d+"
                            onChange={(event) =>
                                onPoliciesConfigurationChanged('policiesHitsThreshold', event.target.value, event)
                            }
                        />
                        <div className={` ${styles.PoliciesHitsTypeSelector}`}>
                            <Dropdown
                                className={`w-[120px] border-b h-8 rounded-none border-sf-gray-300 [&&]:bg-sf-white`}
                                items={Object.values(POLICIES_HITS_TIME_UNITS_TYPES).map((option) => ({
                                    value: option.value,
                                    title: option.label,
                                    disabled: option.disabled
                                }))}
                                fullMenuWidth={120}
                                selectedItems={newConfiguration.policiesHitsTimeUnit}
                                variant="trigger"
                                onChange={(value) => {
                                    onPoliciesConfigurationChanged('policiesHitsTimeUnit', value);
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.policiesConfigurationField}>
                    <div className={styles.policiesConfigurationFieldLabel}>Default tokens</div>
                    <div className={styles.policiesConfigurationFieldData}>
                        <Dropdown
                            name="form-field-name"
                            items={allowedTokensOptions.map((option) => ({
                                value: option.value,
                                title: option.label,
                                disabled: option.disabled,
                                tokenType: option.tokenType,
                                onClick: handleClickTokenOption
                            }))}
                            disableSelectAll
                            multiple={true}
                            selectedItems={selectedTokens}
                            variant="trigger"
                            onChange={(value) => {
                                onTokensAllowedChanged(value);
                            }}
                        />
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className={styles.footerMsg}>{errorMessage}</div>
                <div className={styles.footerButtons}>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button bsStyle={'success'} onClick={SaveConfigurationClicked}>
                        Save
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default PoliciesSettingsModal;
